import { useCallback } from 'react'

import { NumberParam, useQueryParam, withDefault } from 'use-query-params'

/**
 * Hook to easily manage pagination query parameters. The hook will return
 * the page number, rows per page, and an updater to set new page and rowsPerPage.
 *
 *
 * @param id - The id of the query parameter. This is useful when you have multiple pagination on the same page.
 * @returns {Array} - The page, perPage, and updater function
 *
 * @example
 * const [page, perPage, setPageParams] = usePageParams()
 *
 * // Using the updater
 * const handlePageChange = () => setPageParams({ page: 2 })
 */

export default function usePageParams({ id }: { id?: string } = {}): [
	number,
	number,
	({ page }: { page: number }) => void,
] {
	const [page, setPage] = useQueryParam(id ? `${id}-page` : 'page', withDefault(NumberParam, 1), {
		updateType: 'replaceIn',
	})

	// hard coded for now
	const pageSize = 100

	const setPageParams = useCallback(
		({ page }: { page: number }) => {
			setPage(page)
		},
		[setPage]
	)

	return [page, pageSize, setPageParams]
}
